import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Typography,
  Box,
  Grid,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import axios from "axios";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { camelCaseToSentenceCase } from "../../../helpers/camelCaseToSentenceCase";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  LineController,
  Title,
  Tooltip,
  Legend,
  ChartData,
} from "chart.js";
import { Chart } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineController,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
);

enum TimeRange {
  Week = "Week",
  Month = "Month",
  Year = "Year",
}

const fetchTotalMoneyRequests = async (
  range: string,
  token: string | null | undefined,
) => {
  const { data } = await axios.get(
    `${apiVariable}/totalMoneyRequestsForCurrent${range}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return data;
};

const fetchMoneyRequestsByType = async (
  range: string,
  token: string | null | undefined,
) => {
  const { data } = await axios.get(
    `${apiVariable}/moneyRequestsByTypeForCurrent${range}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return data;
};

const fetchMoneyRequestsByMonth = async (
  token: string | null | undefined,
  year: number,
) => {
  const { data } = await axios.get(
    `${apiVariable}/totalMoneyRequestsByMonthForCurrentYear`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: { year },
    },
  );
  return data;
};

const AccountingStatisticsCard = () => {
  const { token } = useAuth();
  const [moneyRequestsRange, setMoneyRequestsRange] = useState<TimeRange>(
    TimeRange.Week,
  );
  const currentYear = new Date().getFullYear();
  const [moneyRequestsGraphYear, setMoneyRequestsGraphYear] =
    useState<number>(currentYear);

  const { data: moneyRequestsData, isLoading: isLoadingMoneyRequests } =
    useQuery({
      queryKey: ["moneyRequestsData", moneyRequestsRange],
      queryFn: () => fetchTotalMoneyRequests(moneyRequestsRange, token),
    });

  const { data: moneyRequestsByType, isLoading: isLoadingMoneyRequestsByType } =
    useQuery({
      queryKey: ["moneyRequestsByType", moneyRequestsRange],
      queryFn: () => fetchMoneyRequestsByType(moneyRequestsRange, token),
    });

  const {
    data: moneyRequestsByMonth,
    isLoading: isLoadingMoneyRequestsByMonth,
  } = useQuery({
    queryKey: ["moneyRequestsByMonth", moneyRequestsGraphYear],
    queryFn: () => fetchMoneyRequestsByMonth(token, moneyRequestsGraphYear),
  });

  const moneyRequestsChartData: ChartData<"bar" | "line"> | null =
    moneyRequestsByMonth
      ? {
          labels: moneyRequestsByMonth.map((m: any) =>
            new Date(2000, m.month - 1).toLocaleString("en-US", {
              month: "short",
            }),
          ),
          datasets: [
            {
              type: "bar" as const,
              label: "Amount ($)",
              data: moneyRequestsByMonth.map((m: any) => m.totalAmount),
              backgroundColor: "rgba(75,192,192,0.6)",
              borderColor: "rgba(75,192,192,1)",
              borderWidth: 1,
              barPercentage: 0.4,
              yAxisID: "yAmount",
            },
            {
              type: "line" as const,
              label: "Requests",
              data: moneyRequestsByMonth.map((m: any) => m.totalRequests),
              borderColor: "rgba(153,102,255,1)",
              backgroundColor: "rgba(153,102,255,0.2)",
              borderWidth: 2,
              tension: 0.3,
              yAxisID: "yCount",
            },
          ],
        }
      : null;

  const moneyChartOptions = {
    responsive: true,
    plugins: {
      legend: { position: "top" as const },
      tooltip: {
        callbacks: {
          label(ctx: any) {
            return ctx.dataset.label === "Amount ($)"
              ? `Amount: $${ctx.raw.toLocaleString()}`
              : `Requests: ${ctx.raw}`;
          },
        },
      },
    },
    scales: {
      x: { title: { display: true, text: "Month" } },
      yCount: {
        type: "linear",
        position: "left",
        title: { display: true, text: "Requests" },
      },
      yAmount: {
        type: "linear",
        position: "right",
        grid: { drawOnChartArea: false },
        title: { display: true, text: "Amount ($)" },
      },
    },
  } as const;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <Grid container spacing={4} maxWidth="lg">
        <Grid
          item
          xs={12}
          md={6}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              backgroundColor: "#ffffff",
              textAlign: "center",
              flex: 1,
            }}
          >
            <CardHeader
              className="responsive-card-header"
              title="Issued Money Codes"
              titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
              action={
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={moneyRequestsRange}
                    onChange={(e) =>
                      setMoneyRequestsRange(e.target.value as TimeRange)
                    }
                  >
                    <MenuItem value={TimeRange.Week}>1 Week</MenuItem>
                    <MenuItem value={TimeRange.Month}>1 Month</MenuItem>
                    <MenuItem value={TimeRange.Year}>1 Year</MenuItem>
                  </Select>
                </FormControl>
              }
            />
            <CardContent>
              {isLoadingMoneyRequests ? (
                <Typography>Loading...</Typography>
              ) : (
                <Typography variant="h4" color="primary">
                  {moneyRequestsData}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              backgroundColor: "#ffffff",
              textAlign: "center",
              flex: 1,
            }}
          >
            <CardHeader
              className="responsive-card-header"
              title="Issued Money Codes by Type"
              titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
            />
            <CardContent>
              {isLoadingMoneyRequestsByType ? (
                <Typography>Loading...</Typography>
              ) : moneyRequestsByType && moneyRequestsByType.length > 0 ? (
                moneyRequestsByType.map((item: any) => (
                  <Typography key={item.type}>
                    {`${camelCaseToSentenceCase(item.moneyRequestType)}: ${
                      item.totalMoneyRequests
                    }`}
                  </Typography>
                ))
              ) : (
                <Typography>No data available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sx={{ display: "flex", flexDirection: "column" }}>
          <Card
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              backgroundColor: "#ffffff",
              textAlign: "center",
            }}
          >
            <CardHeader
              className="responsive-card-header"
              title="Money Requests (Count vs Amount)"
              titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
              action={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setMoneyRequestsGraphYear((previous) => previous - 1)
                    }
                    aria-label="Previous money requests year"
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <Typography variant="body2" fontWeight={600}>
                    {moneyRequestsGraphYear}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setMoneyRequestsGraphYear((previous) =>
                        Math.min(previous + 1, currentYear),
                      )
                    }
                    disabled={moneyRequestsGraphYear >= currentYear}
                    aria-label="Next money requests year"
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </Box>
              }
            />
            <CardContent>
              {isLoadingMoneyRequestsByMonth ? (
                <Typography>Loading...</Typography>
              ) : moneyRequestsChartData ? (
                <Chart
                  type="bar"
                  data={moneyRequestsChartData}
                  options={moneyChartOptions}
                  height={100}
                  key={JSON.stringify(moneyRequestsChartData)}
                />
              ) : (
                <Typography>No data available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AccountingStatisticsCard;
